import { Injectable } from '@angular/core';

import type { ListItem } from 'src/app/components/scrolling-list';
import { ModalService } from 'src/app/components/modals/modal.service';
import { StorageManagerService } from './storage-manager.service';
import { StorageUtilsService, type StorageFile } from './storage-utils.service';
import { UploadOverwriteWarningService } from './upload-overwrite-warning.service';
import { StorageApiService } from 'src/app/api/services/storage-api.service';
import { FileUploaderService } from './file-uploader.service';
import { UploadUriService } from './upload-uri.service';
import { FileCheckerService } from './file-checker.service';

export type UploadManagerConfig = {
  onUploadStatus: Function;
  addFile?: Function;
  validType?: string;
  detectChanges?: Function;
  getFolderPath?: Function;
  getExistingFiles?: () => ListItem<StorageFile>[];
  skipOverwrite?: boolean;
  alwaysOverwrite?: boolean;
  onCompleteItem?: Function;
  onProgressItem?: Function;
  isSingleFileSelector?: Function;
  folderPath?: string;
};

@Injectable({
  providedIn: 'root',
})
export class UploadManagerService {
  static readonly STORAGE_UPLOAD_CHUNK_SIZE = 256 * 1024 * 100; // Multiple of Google required chunk size (256 * 1024)
  static readonly KEEP_BOTH_MAX_STALE_LIST_RETRIES = 2;

  constructor(
    private modalService: ModalService,
    private storageApiService: StorageApiService,
    private storageManagerService: StorageManagerService,
    private fileUploaderService: FileUploaderService,
    private uploadUriService: UploadUriService,
    private uploadOverwriteWarningService: UploadOverwriteWarningService,
    private fileCheckerService: FileCheckerService,
    private storageUtilsService: StorageUtilsService,
  ) {}

  private async fetchTakenNames(filePath: string): Promise<Set<string>> {
    const folderEnd = filePath.lastIndexOf('/') + 1;
    const dotIndex = filePath.slice(folderEnd).indexOf('.');
    const prefix =
      dotIndex > 0 ? filePath.slice(0, folderEnd + dotIndex) : filePath;

    const resp = await this.storageApiService.getFolderContents(prefix);

    return new Set<string>(
      (resp?.items || [])
        .filter((item) => item && !item.folder)
        .map((item) => item.name || item.objectId)
        .filter((name) => !!name && !name.endsWith('/')),
    );
  }

  private async resolveFreeName(
    file,
    batchTakenNames?: Set<string> | null,
    knownNames?: Set<string>,
    staleListRetries = 0,
  ): Promise<any> {
    const originalName = file.name;

    try {
      const names = knownNames || (await this.fetchTakenNames(originalName));
      const taken = batchTakenNames
        ? new Set([...names, ...batchTakenNames])
        : names;
      const { name } = this.storageUtilsService.getFreeRenameCandidate(
        originalName,
        taken,
      );
      file.name = name;

      const fileMetadataResponse = await this.uploadUriService.getUri(file);
      if (
        fileMetadataResponse.result &&
        fileMetadataResponse.isOverwrite === false
      ) {
        batchTakenNames?.add(name);
        return fileMetadataResponse;
      }

      if (
        staleListRetries >=
        UploadManagerService.KEEP_BOTH_MAX_STALE_LIST_RETRIES
      ) {
        throw {
          message: 'No available file name',
        };
      }

      // The listing was stale: another upload took the candidate before
      // getUri() confirmed it. Refetch and recompute instead of guessing
      // candidate names one by one.
      file.name = originalName;
      return this.resolveFreeName(
        file,
        batchTakenNames,
        undefined,
        staleListRetries + 1,
      );
    } catch (e) {
      file.name = originalName;
      throw e;
    }
  }

  getFileUploader(config: UploadManagerConfig) {
    let fileTypeWarned;

    let validExtensions = this.storageManagerService.getValidExtensionsList(
      config.validType,
    );

    let FileUploader = this.fileUploaderService.getUploader();

    FileUploader._showInvalidExtensionsMessage = () => {
      var title = 'This file type is not supported';
      var message = FileUploader._getValidExtensionsMessage(validExtensions);

      this.modalService.showMessage(title, message);
    };

    FileUploader._getValidExtensionsMessage = (validExtensions) => {
      var prefix = validExtensions;
      var suffix = '';

      if (validExtensions.length > 1) {
        prefix = validExtensions.slice(0, validExtensions.length - 1);
        suffix =
          ' and ' + validExtensions[validExtensions.length - 1].toUpperCase();
      }

      return (
        'Rise Vision supports ' + prefix.join(', ').toUpperCase() + suffix + '.'
      );
    };

    let checkHeicFileType = (fileItem) => {
      const isHeic =
        fileItem.name?.toLowerCase().endsWith('.heic') ||
        fileItem.file?.name?.toLowerCase().endsWith('.heic');

      if (isHeic && !fileTypeWarned) {
        fileTypeWarned = true;
        this.modalService.showMessage(
          'This file type is not supported',
          "Rise Vision doesn't support HEIC files.",
        );
      }

      return isHeic;
    };

    let checkFileType = (fileItem) => {
      if (checkHeicFileType(fileItem)) {
        return true;
      }

      if (!validExtensions.length) {
        return;
      }

      if (
        this.storageManagerService.fileHasValidExtension(
          fileItem.name || fileItem.file.name,
          validExtensions,
        )
      ) {
        return;
      }

      if (
        fileItem.file &&
        fileItem.file.type &&
        fileItem.file.type.indexOf('video') === 0 &&
        fileItem.encodingFileName
      ) {
        return;
      }

      if (fileTypeWarned) {
        return true;
      }
      fileTypeWarned = true;
      FileUploader._showInvalidExtensionsMessage();
      return true;
    };

    FileUploader.currentFilePath = () => {
      return config.getFolderPath();
    };

    FileUploader.activeUploadCount = () => {
      return FileUploader.queue.filter((item) => item.isUploading).length;
    };

    FileUploader._isUploading = () => {
      return FileUploader.activeUploadCount() > 0;
    };

    if (FileUploader.notifyProgressItem) {
      const originalNotifyProgressItem =
        FileUploader.notifyProgressItem.bind(FileUploader);
      FileUploader.notifyProgressItem = (item, progress) => {
        originalNotifyProgressItem(item, progress);
        if (config.onProgressItem) {
          config.onProgressItem(item, progress);
        }
      };
    } else {
      FileUploader.notifyProgressItem = (item, progress) => {
        if (config.onProgressItem) {
          config.onProgressItem(item, progress);
        }
      };
    }

    FileUploader.removeItem = (item) => {
      FileUploader.removeFromQueue(item);
      config.onUploadStatus(FileUploader._isUploading());
    };

    FileUploader.retryFailedUpload = (file) => {
      if (file.isError) {
        FileUploader.retryItem(file);
      }
    };

    FileUploader.retryFailedUploads = () => {
      FileUploader.queue.forEach((file) => {
        FileUploader.retryFailedUpload(file);
      });
    };

    FileUploader.cancelAllUploads = () => {
      FileUploader.removeAll();
      config.onUploadStatus(FileUploader._isUploading());
    };

    let batchTakenNames: Set<string> | null = null;

    const getExistingFileNames = (): Set<string> | null => {
      if (
        config.skipOverwrite ||
        config.alwaysOverwrite ||
        !config.getExistingFiles
      ) {
        return null;
      }

      const existingFiles = config.getExistingFiles();

      if (!existingFiles) {
        return null;
      }

      return new Set(
        existingFiles
          .map((entry) => entry?.item?.name)
          .filter((name) => !!name && !name.endsWith('/')),
      );
    };

    const wouldBeRejected = (fileItem) => {
      if (fileItem.file.name.toLowerCase().endsWith('.heic')) {
        return true;
      }

      if (!validExtensions.length) {
        return false;
      }

      if (
        this.storageManagerService.fileHasValidExtension(
          fileItem.file.name,
          validExtensions,
        )
      ) {
        return false;
      }

      return !(fileItem.file.type && fileItem.file.type.indexOf('video') === 0);
    };

    const enqueue = (fileItems) =>
      Promise.resolve(FileUploader.addToQueue(fileItems));

    FileUploader.uploadSelectedFiles = async (selectedFiles) => {
      batchTakenNames = null;
      const fileItems = await FileUploader.compress(selectedFiles);
      const existingNames = getExistingFileNames();

      if (!existingNames) {
        return enqueue(fileItems);
      }

      const folderPath = config.getFolderPath() || '';
      const conflicting = fileItems.filter((fileItem) => {
        const isFile = fileItem.file.size || fileItem.file.type;
        const skipsConfirmation =
          fileItem.file.name.startsWith('canva/') ||
          fileItem.file.name.startsWith('.assets/canva/');

        return (
          isFile &&
          !skipsConfirmation &&
          !wouldBeRejected(fileItem) &&
          existingNames.has(folderPath + fileItem.file.name)
        );
      });

      if (!conflicting.length) {
        return enqueue(fileItems);
      }

      const clear = fileItems.filter(
        (fileItem) => !conflicting.includes(fileItem),
      );
      const queued = clear.length ? enqueue(clear) : Promise.resolve(true);

      let choice;
      try {
        choice = await this.uploadOverwriteWarningService.requestBatchDecision(
          conflicting.map((fileItem) => fileItem.file.name),
        );
      } catch {
        return queued;
      }

      if (choice === 'keepBoth') {
        batchTakenNames = batchTakenNames || new Set();
        conflicting.forEach((fileItem) => {
          const { name } = this.storageUtilsService.getFreeRenameCandidate(
            folderPath + fileItem.file.name,
            new Set([...existingNames, ...batchTakenNames]),
          );
          batchTakenNames.add(name);
          fileItem.overwriteDecision = 'keepBoth';
          fileItem.preRenameName = fileItem.file.name;
          fileItem.file.name = name.slice(folderPath.length);
        });
      } else {
        conflicting.forEach((fileItem) => {
          fileItem.overwriteDecision = 'replace';
        });
      }

      await enqueue(conflicting);
      return queued;
    };

    FileUploader.onAddingFiles = () => {
      this.uploadOverwriteWarningService.reset();
    };

    FileUploader.onAfterAddingFile = (fileItem) => {
      console.info('onAfterAddingFile', fileItem.file.name);

      if (!fileItem.isRetrying) {
        fileItem.file.name =
          (config.getFolderPath() || '') + fileItem.file.name;
      }

      if (fileItem.conversionError) {
        this.modalService.showMessage(
          'Invalid subtitle file',
          fileItem.conversionError,
        );
        FileUploader.removeFromQueue(fileItem);
        config.onUploadStatus(FileUploader._isUploading());
        return;
      }

      config.onUploadStatus(FileUploader._isUploading());

      let checkSettled = false;
      const settleOverwriteCheck = () => {
        if (!checkSettled) {
          checkSettled = true;
          this.uploadOverwriteWarningService.completePendingCheck();
        }
      };

      const removeItem = () => {
        FileUploader.removeFromQueue(fileItem);
        config.onUploadStatus(FileUploader._isUploading());
      };

      const applyUriResponse = (resp) => {
        fileItem.url = resp.message;
        fileItem.taskToken = resp.taskToken;
        fileItem.encodingFileName = resp.newFileName;
        fileItem.chunkSize = UploadManagerService.STORAGE_UPLOAD_CHUNK_SIZE;
      };

      var uploadFile = (resp) => {
        applyUriResponse(resp);

        if (checkFileType(fileItem)) {
          settleOverwriteCheck();
          removeItem();

          return;
        }

        if (config.skipOverwrite || config.alwaysOverwrite) {
          settleOverwriteCheck();
          if (resp.isOverwrite && !config.alwaysOverwrite) {
            removeItem();
            config.addFile(fileItem.file);
          } else {
            FileUploader.uploadItem(fileItem, config.detectChanges);
          }
          return;
        }

        const skipConfirmation =
          fileItem.file.name.startsWith('canva/') ||
          fileItem.file.name.startsWith('.assets/canva/');

        if (!resp.isOverwrite || skipConfirmation) {
          settleOverwriteCheck();
          FileUploader.uploadItem(fileItem, config.detectChanges);
          return;
        }

        const folderPath = config.getFolderPath() || '';

        if (fileItem.overwriteDecision === 'replace') {
          settleOverwriteCheck();
          FileUploader.uploadItem(fileItem, config.detectChanges);
          return;
        }

        if (fileItem.overwriteDecision === 'keepBoth') {
          settleOverwriteCheck();
          fileItem.file.name = folderPath + fileItem.preRenameName;
          batchTakenNames = batchTakenNames || new Set();
          return this.resolveFreeName(fileItem.file, batchTakenNames).then(
            (uniqueResp) => {
              applyUriResponse(uniqueResp);
              FileUploader.uploadItem(fileItem, config.detectChanges);
            },
            (error) => {
              FileUploader.notifyErrorItem(fileItem, error?.status);
            },
          );
        }
        const displayName = fileItem.file.name.startsWith(folderPath)
          ? fileItem.file.name.slice(folderPath.length)
          : fileItem.file.name;

        const decision =
          this.uploadOverwriteWarningService.requestDecision(displayName);
        settleOverwriteCheck();

        return decision.then(
          (choice) => {
            if (choice === 'keepBoth') {
              batchTakenNames = batchTakenNames || new Set();
              return this.resolveFreeName(fileItem.file, batchTakenNames).then(
                (uniqueResp) => {
                  applyUriResponse(uniqueResp);
                  FileUploader.uploadItem(fileItem, config.detectChanges);
                },
                (error) => {
                  FileUploader.notifyErrorItem(fileItem, error?.status);
                },
              );
            }

            FileUploader.uploadItem(fileItem, config.detectChanges);
          },
          () => {
            removeItem();
          },
        );
      };

      this.uploadOverwriteWarningService.registerPendingCheck();

      return this.uploadUriService
        .getUri(fileItem.file)
        .then(uploadFile)
        .catch((resp) => {
          console.log('getURI error', resp);

          if (resp.message === 'Unencodable overwrite') {
            return this.uploadUriService
              .getUri(fileItem.file, true)
              .then(uploadFile)
              .catch((retryResp) => {
                settleOverwriteCheck();
                FileUploader.notifyErrorItem(fileItem, retryResp.status);
              });
          }

          settleOverwriteCheck();
          FileUploader.notifyErrorItem(fileItem, resp.status);
        });
    };

    FileUploader.onBeforeUploadItem = (item) => {
      console.log('Attempting to upload', item.file.name);
      config.onUploadStatus(FileUploader._isUploading());
    };

    FileUploader.onCancelItem = (item) => {
      FileUploader.removeFromQueue(item);
      config.onUploadStatus(FileUploader._isUploading());
    };

    FileUploader.onCompleteItem = (item) => {
      if (item.isCancel || !item.isSuccess) {
        if (config.onCompleteItem) {
          config.onCompleteItem(item);
        }
        config.onUploadStatus(FileUploader._isUploading());

        if (!item.isSuccess) {
          console.log('Failed to upload: ', item.file);
        }

        return;
      }

      var baseFile = {
        name: item.encodingFileName || item.file.name,
        updated: {
          value: new Date().valueOf().toString(),
        },
        size: item.file.size,
        type: item.file.type,
      };

      // Retrieve to force thumbnail creation
      this.storageApiService
        .refreshFileMetadata(item.encodingFileName || item.file.name)
        .then(
          (file) => {
            this.fileCheckerService.setCachedFile(file);

            config.addFile(file);
          },
          (err) => {
            console.log('Error refreshing metadata', item?.file?.name, err);

            this.fileCheckerService.setCachedFile(baseFile);

            config.addFile(baseFile);
          },
        )
        .finally(() => {
          FileUploader.removeFromQueue(item);
          if (config.onCompleteItem) {
            config.onCompleteItem(item);
          }
          config.onUploadStatus(FileUploader._isUploading());
          config.detectChanges();
        });
    };

    return FileUploader;
  }
}
